import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import style from './weight.module.less';
import { CSWFieldId, getOrderDetailsFieldId } from '../../../../../constants/field-ids';
import { UnitDisplayType } from '../../../../../enums/units/unit-display-type';
import { UnitPosition } from '../../../../../enums/units/unit-position';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import WeightContainer from '../../../../../fulfillment-planner/weight/input/container';
import Scale from '../../../../../fulfillment-planner/weight/scale';
import { useReadFromScale } from '../../../../../hooks/use-read-from-scale';
import messages from '../../../fulfillment-plan/sections/configure-shipment/messages';
import { WeightLabel } from '../../../shared-field-labels/shared-field-labels';
import fieldStyle from '../fields.module.less';
import LabeledField from '../labeled-field';
import { usePackingApplied } from '../packing-recommend/use-packing-applied';
import { FieldLabelWithIndicators } from '../packing-recommend/components/field-label-with-indicators/field-label-with-indicators';
import packingStyle from '../packing-recommend/packing-applied-classes.module.less';

import buttonTextStyle from 'ui-components/components/button/button-text.module.less';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';
import smartFillStyle from 'ui-components/components/smartfill.module.less';

const fieldId = getOrderDetailsFieldId(CSWFieldId.Weight);

/**
 * @param {object} props
 * @param {string?} [props.containerClassName]
 * @param {boolean?} [props.isRequired]
 * @param {string?} [props.fulfillmentPlanId]
 * @returns {JSX.Element}
 */
const Weight = ({ containerClassName = '', isRequired = false, fulfillmentPlanId = '' }) => {
  const {
    onWeightAndDimensionsChange,
    packages = [],
    hasSmartFillDataBeenChanged,
    isSmartFillEnabled,
    smartFillMetaData,
  } = useFulfillmentPlannerContext();
  const { isPackingApplied } = usePackingApplied(fulfillmentPlanId);
  const onScaleMessage = useCallback(
    (weightAndDims) => {
      onWeightAndDimensionsChange(weightAndDims?.weight, weightAndDims?.dims);
    },
    [onWeightAndDimensionsChange],
  );
  const onReadFromScale = useReadFromScale(onScaleMessage);

  const hasSmartFillWeightChanged = hasSmartFillDataBeenChanged?.weight;
  const shouldShowSmartFillIcon = isSmartFillEnabled && !hasSmartFillWeightChanged;

  return (
    <LabeledField
      label={
        <FieldLabelWithIndicators
          isPackingApplied={isPackingApplied}
          shouldShowSmartFillIcon={shouldShowSmartFillIcon}
          smartFillCount={smartFillMetaData?.Weight?.TimesUsed}
        >
          <LabelWithRequiredIndicator required={isRequired}>{WeightLabel}</LabelWithRequiredIndicator>
        </FieldLabelWithIndicators>
      }
      labelId={fieldId}
    >
      <div className={classnames(style['weight-field'], containerClassName, { [packingStyle['packing-applied-field']]: isPackingApplied })}>
        <WeightContainer
          className={classnames(style['weight-input-outer-container'], { [style['packing-applied-input']]: isPackingApplied })}
          inputClassName={classnames(
            fieldStyle['number-input'],
            style['weight-input'],
            { [smartFillStyle['smart-fill']]: shouldShowSmartFillIcon },
            {
              [smartFillStyle['smart-fill-container']]: shouldShowSmartFillIcon,
            },
          )}
          labelId={fieldId}
          numberInputContainerClassName={classnames(style['number-input-container'], {
            [smartFillStyle['smart-fill-container']]: shouldShowSmartFillIcon,
          })}
          unitDisplayType={UnitDisplayType.Abbreviated}
          unitPosition={UnitPosition.InsideStart}
          weightInputInnerContainerClassName={style['weight-input-inner-container']}
        />
        <Scale
          buttonClassName={classnames(style['scale-button'], buttonTextStyle['button-text'])}
          buttonIconClassName={style['scale-icon']}
          buttonText={<FormattedMessage {...messages.scale} />}
          isSmartFillApplied={shouldShowSmartFillIcon}
          onReadFromScale={onReadFromScale}
          packageCount={packages.length}
        />
      </div>
    </LabeledField>
  );
};

export default Weight;
